import React from 'react';
import { Tooltip, Typography } from 'antd';
import { formatNumber } from '@/utils/format';

const { Text } = Typography;

const PALETTE = ['#2E7D5B', '#E0A526', '#E07B26', '#D9542C', '#B83A2E', '#7F1D1D', '#5B1414'];

export interface AgeingBarBucket {
  key: string;
  label: string;
  amount: number;
  overdue?: boolean;
  color?: string;
}

interface Props {
  buckets: AgeingBarBucket[];
  format: (n: number) => string;
  /** Extra legend entries not drawn on the bar (e.g. unapplied credits). */
  extras?: Array<{ label: string; amount: number; color?: string }>;
}

const pct = (part: number, total: number) => formatNumber((part / total) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const AgeingBar: React.FC<Props> = ({ buckets, format, extras = [] }) => {
  const positive = buckets.map((b, i) => ({ ...b, color: b.color ?? PALETTE[Math.min(i, PALETTE.length - 1)], amount: Math.max(0, b.amount ?? 0) }));
  const total = positive.reduce((t, b) => t + b.amount, 0);

  return (
    <div>
      <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: '#EEF2F6' }} aria-label="Ageing of balance">
        {total > 0 &&
          positive
            .filter((b) => b.amount > 0)
            .map((b) => (
              <Tooltip key={b.key} title={`${b.label}: ${format(b.amount)} (${pct(b.amount, total)}%)`}>
                <div style={{ width: `${(b.amount / total) * 100}%`, background: b.color, minWidth: 4 }} />
              </Tooltip>
            ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 10 }}>
        {positive.map((b) => (
          <div key={b.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: b.color, marginTop: 4, flex: 'none' }} />
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {b.label}
              </Text>
              <div>
                <Text strong style={{ color: b.overdue && b.amount > 0 ? '#B42318' : undefined }}>
                  {format(b.amount)}
                </Text>
              </div>
            </div>
          </div>
        ))}
        {extras
          .filter((e) => e.amount !== 0)
          .map((e) => (
            <div key={e.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: e.color ?? '#8DA2B8', marginTop: 4, flex: 'none' }} />
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {e.label}
                </Text>
                <div>
                  <Text strong style={{ color: '#1E7B4F' }}>
                    {format(e.amount)}
                  </Text>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default AgeingBar;
